import { useState, useEffect } from 'react';
import { Video, XCircle, Loader2, AlertTriangle, Unlink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { zoomService, type VideoConnection, type VideoProvider } from '../../services/zoomService';
import { calendarService } from '../../services/calendarService';
import type { CalendarProvider } from '../../types/calendar';

interface SalesTeamMember {
  id: string;
  client_id: string;
  name: string;
  email: string;
  is_active: boolean;
}

interface CalendarConnectionInfo {
  id: string;
  provider: CalendarProvider;
  calendar_email: string;
  is_active: boolean;
}

interface ProviderOption {
  id: VideoProvider;
  name: string;
  iconSrc: string;
}

const ALL_PROVIDERS: ProviderOption[] = [
  { id: 'google_meet', name: 'Google Meet', iconSrc: '/google-meets-icon.svg' },
  { id: 'microsoft_teams', name: 'Microsoft Teams', iconSrc: '/microsoft-teams-icon.svg' },
  { id: 'zoom', name: 'Zoom', iconSrc: '/zoom-meetings-icon.svg' },
];

function getAvailableProviders(calendarProvider: CalendarProvider | null): ProviderOption[] {
  if (calendarProvider === 'google') {
    return ALL_PROVIDERS.filter(p => p.id === 'google_meet' || p.id === 'zoom');
  }
  if (calendarProvider === 'microsoft') {
    return ALL_PROVIDERS.filter(p => p.id === 'microsoft_teams' || p.id === 'zoom');
  }
  return [];
}

const STORAGE_KEYS: Record<string, string> = {
  'zoom-oauth': 'oauth-result-zoom',
  'google-meet-oauth': 'oauth-result-google-meet',
  'microsoft-teams-oauth': 'oauth-result-microsoft-teams',
};

export function VideoCallsSection() {
  const { user } = useAuth();
  const [members, setMembers] = useState<SalesTeamMember[]>([]);
  const [calendarConnections, setCalendarConnections] = useState<Record<string, CalendarConnectionInfo | null>>({});
  const [videoConnections, setVideoConnections] = useState<Record<string, VideoConnection | null>>({});
  const [loading, setLoading] = useState(true);
  const [connectingMember, setConnectingMember] = useState<string | null>(null);
  const [disconnectingMember, setDisconnectingMember] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!user?.client_id) return;
    try {
      setLoading(true);
      const membersList = await calendarService.getSalesTeamMembers(user.client_id);
      setMembers(membersList);

      const calConns: Record<string, CalendarConnectionInfo | null> = {};
      const vidConns: Record<string, VideoConnection | null> = {};

      const memberIds = membersList.map((m: SalesTeamMember) => m.id);

      const [calResults, videoResults] = await Promise.all([
        Promise.all(membersList.map((m: SalesTeamMember) =>
          calendarService.getCalendarConnectionForMember(user.client_id!, m.id)
        )),
        memberIds.length > 0
          ? zoomService.getVideoConnectionsForMembers(user.client_id, memberIds)
          : Promise.resolve([]),
      ]);

      membersList.forEach((m: SalesTeamMember, i: number) => {
        calConns[m.id] = calResults[i] || null;
      });

      const videoByMember: Record<string, VideoConnection | null> = {};
      for (const m of membersList) {
        videoByMember[m.id] = (videoResults as VideoConnection[]).find(v => v.sales_member_id === m.id) || null;
      }

      setCalendarConnections(calConns);
      setVideoConnections(videoByMember);
    } catch (err) {
      console.error('Error loading video calls data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const openOAuthPopup = (authUrl: string, messageType: string, providerName: string, memberId: string) => {
    setConnectingMember(memberId);
    setError(null);

    const storageKey = STORAGE_KEYS[messageType];
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }

    try {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        `${messageType}-popup`,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      let timeoutId: number | null = null;
      let pollId: number | null = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (pollId) clearInterval(pollId);
      };

      pollId = window.setInterval(() => {
        if (popup && popup.closed) {
          const raw = storageKey ? localStorage.getItem(storageKey) : null;
          if (!raw) {
            cleanup();
            setConnectingMember(null);
            return;
          }
        }

        if (!storageKey) return;
        const raw = localStorage.getItem(storageKey);
        if (!raw) return;

        cleanup();
        localStorage.removeItem(storageKey);

        try {
          const data = JSON.parse(raw);
          if (data.success) {
            loadData();
          } else {
            setError(data.error || `Failed to connect ${providerName}`);
          }
        } catch {
          setError(`Failed to connect ${providerName}`);
        }
        setConnectingMember(null);
      }, 500);

      timeoutId = window.setTimeout(() => {
        cleanup();
        setConnectingMember(null);
        setError('Connection timed out. Please try again.');
      }, 120000);
    } catch (err) {
      console.error(`Error initiating ${providerName} OAuth:`, err);
      setError('Failed to start connection process.');
      setConnectingMember(null);
    }
  };

  const handleConnect = async (provider: VideoProvider, memberId: string) => {
    if (!user?.client_id) return;

    try {
      if (provider === 'zoom') {
        const authUrl = await zoomService.initiateZoomOAuth(user.client_id, user.email || '', memberId);
        openOAuthPopup(authUrl, 'zoom-oauth', 'Zoom', memberId);
      } else if (provider === 'google_meet') {
        const authUrl = await zoomService.initiateGoogleMeetOAuth(user.client_id, user.email || '', memberId);
        openOAuthPopup(authUrl, 'google-meet-oauth', 'Google Meet', memberId);
      } else if (provider === 'microsoft_teams') {
        const authUrl = await zoomService.initiateMicrosoftTeamsOAuth(user.client_id, user.email || '', memberId);
        openOAuthPopup(authUrl, 'microsoft-teams-oauth', 'Microsoft Teams', memberId);
      }
    } catch (err) {
      console.error('Error initiating video OAuth:', err);
      setError('Failed to start connection process.');
      setConnectingMember(null);
    }
  };

  const handleDisconnect = async (memberId: string) => {
    if (!user?.client_id) return;
    const vidConn = videoConnections[memberId];
    if (!vidConn) return;
    if (!confirm('Are you sure you want to disconnect this video provider?')) return;

    try {
      setDisconnectingMember(memberId);
      let result;
      if (vidConn.provider === 'google_meet') {
        result = await zoomService.disconnectGoogleMeet(user.client_id, memberId);
      } else if (vidConn.provider === 'microsoft_teams') {
        result = await zoomService.disconnectMicrosoftTeams(user.client_id, memberId);
      } else {
        result = await zoomService.disconnectZoom(user.client_id, memberId);
      }
      if (result.success) {
        setVideoConnections(prev => ({ ...prev, [memberId]: null }));
      } else {
        setError(result.error || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Failed to disconnect');
    } finally {
      setDisconnectingMember(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
        </div>
      </div>
    );
  }

  const membersWithCalendars = members.filter(m => calendarConnections[m.id] != null);

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {membersWithCalendars.length > 0 && Object.values(videoConnections).every(v => v == null) && (
        <div className="p-3.5 bg-red-50 rounded-xl border border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">
              No video providers connected yet. Connect a video provider for each team member to automatically generate meeting links.
            </p>
          </div>
        </div>
      )}

      {membersWithCalendars.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Video className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-2">No calendars connected</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Connect a calendar for your sales team members first. Video providers will become available based on their calendar type.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="p-3.5 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-start gap-3">
              <Video className="w-5 h-5 text-[#1875FF] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                Each team member gets their own video provider. Meeting links are automatically generated using the assigned member's account.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
            {membersWithCalendars.map((member) => {
              const calConn = calendarConnections[member.id];
              const vidConn = videoConnections[member.id];
              const availableProviders = getAvailableProviders(calConn?.provider || null);
              const isConnecting = connectingMember === member.id;
              const isDisconnecting = disconnectingMember === member.id;

              return (
                <MemberVideoRow
                  key={member.id}
                  member={member}
                  videoConnection={vidConn}
                  availableProviders={availableProviders}
                  isConnecting={isConnecting}
                  isDisconnecting={isDisconnecting}
                  onConnect={(provider) => handleConnect(provider, member.id)}
                  onDisconnect={() => handleDisconnect(member.id)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MemberVideoRow({
  member,
  videoConnection,
  availableProviders,
  isConnecting,
  isDisconnecting,
  onConnect,
  onDisconnect,
}: {
  member: SalesTeamMember;
  videoConnection: VideoConnection | null;
  availableProviders: ProviderOption[];
  isConnecting: boolean;
  isDisconnecting: boolean;
  onConnect: (provider: VideoProvider) => void;
  onDisconnect: () => void;
}) {
  const connectedProvider = videoConnection
    ? ALL_PROVIDERS.find(p => p.id === videoConnection.provider)
    : null;

  return (
    <div className="px-5 py-4 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-gray-900 truncate">{member.name}</h4>
        <span className="text-xs text-gray-400 truncate block">{member.email}</span>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {videoConnection && connectedProvider ? (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-green-50 border-green-200">
              <span className="w-2 h-2 rounded-full animate-pulse bg-green-500" />
              <img src={connectedProvider.iconSrc} alt={connectedProvider.name} className="w-3.5 h-3.5" />
              <span className="text-xs font-medium text-green-700">{connectedProvider.name}</span>
            </div>
            <button
              onClick={onDisconnect}
              disabled={isDisconnecting}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
              title="Disconnect"
            >
              {isDisconnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Unlink className="w-4 h-4" />
              )}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-red-50 border-red-200">
              <span className="w-2 h-2 rounded-full animate-pulse bg-red-500" />
              <span className="text-xs font-medium text-red-700">Not connected</span>
            </div>
            {availableProviders.map((provider) => (
              <button
                key={provider.id}
                onClick={() => onConnect(provider.id)}
                disabled={isConnecting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:border-[#1875FF] hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title={`Connect ${provider.name}`}
              >
                {isConnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : (
                  <img src={provider.iconSrc} alt={provider.name} className="w-4 h-4" />
                )}
                <span className="text-xs font-medium text-gray-700">{provider.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
